import { apiSlice } from "@/services/api-slice";
import { APIResponse } from "@/types/custom-http";
import * as inter from "./certification-records.interface";

const buildListParams = (params?: {
  search: string;
  year?: string | number | null;
  page?: number;
  size?: number;
}) => {
  const finalParams: Record<string, string | number> = {};
  if (params) {
    finalParams.search = params.search.trim();
  }
  if (params?.year !== undefined && params.year !== null && String(params.year).trim().length > 0) {
    finalParams.year = Number(params.year);
  }
  if (typeof params?.page === "number" && params.page > 0) {
    finalParams.page = params.page;
  }
  if (typeof params?.size === "number" && params.size > 0) {
    finalParams.size = params.size;
  }
  return Object.keys(finalParams).length > 0 ? finalParams : undefined;
};

const emptyPaginatedResponse = <T>(message: string, page = 1, size = 20, result = 501): APIResponse<inter.PaginatedData<T>> => ({
  success: false,
  result,
  message,
  data: {
    page,
    size,
    total: 0,
    totalPages: 1,
    data: [],
  },
});

export const certificationRecordsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCertificationRecordSettings: builder.query<APIResponse<inter.CertificationRecordSettings>, void>({
      query: () => ({
        url: "auth/certification-records/settings",
      }),
      providesTags: ["CertificationRecordSettings"],
    }),

    getCertificationRecords: builder.query<
      APIResponse<inter.PaginatedData<inter.CertificationRecordView>>,
      { search: string; year?: string | number | null; page: number; size: number }
    >({
      query: (params) => ({
        url: "auth/certification-records",
        params: buildListParams(params),
      }),
      providesTags: ["CertificationRecords"],
    }),

    getGeneratedCertificationRecords: builder.query<
      APIResponse<inter.PaginatedData<inter.CertificationRecordView>>,
      { search: string; year?: string | number | null; page: number; size: number }
    >({
      query: (params) => ({
        url: "auth/certification-records/generated",
        params: buildListParams(params),
      }),
      providesTags: ["CertificationRecords"],
    }),

    getRevisionCertificationRecords: builder.query<
      APIResponse<inter.PaginatedData<inter.CertificationRecordView>>,
      { search: string; year?: string | number | null; page: number; size: number }
    >({
      query: (params) => ({
        url: "auth/certification-records/revision",
        params: buildListParams(params),
      }),
      providesTags: ["CertificationRecords"],
    }),

    getCertificationRecordById: builder.query<APIResponse<inter.CertificationRecordDetails>, number>({
      query: (id) => ({
        url: `auth/certification-records/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: "CertificationRecord", id }],
    }),

    getCertificationRecordDetail: builder.query<APIResponse<inter.CertificationRecordDetails>, number>({
      query: (id) => ({
        url: `auth/certification-records/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: "CertificationRecord", id }],
    }),

    getEligibleCertificationsForRecord: builder.query<
      APIResponse<inter.PaginatedData<inter.EligibleCertificationForRecord>>,
      { search?: string | null; page?: number; size?: number; record_id?: number | null } | void
    >({
      async queryFn(params, _api, _extraOptions, baseQuery) {
        const finalParams: Record<string, string | number> = buildListParams({
          search: params?.search?.trim() ?? "",
          page: params?.page,
          size: params?.size,
        }) ?? {};

        if (params?.record_id) {
          finalParams.record_id = params.record_id;
        }

        const result = await baseQuery({
          url: "auth/certification-records/eligible-certifications",
          params: finalParams,
        });

        if ("data" in result && result.data) {
          return { data: result.data as APIResponse<inter.PaginatedData<inter.EligibleCertificationForRecord>> };
        }

        if ("error" in result && result.error && (result.error.status === 404 || result.error.status === 501)) {
          return {
            data: emptyPaginatedResponse<inter.EligibleCertificationForRecord>(
              "El endpoint de certificaciones elegibles para actas todavía no está disponible en backend.",
              params?.page ?? 1,
              params?.size ?? 20,
              Number(result.error.status)
            ),
          };
        }

        return {
          error: result.error ?? {
            status: "CUSTOM_ERROR",
            error: "Eligible certifications query failed",
            data: "Eligible certifications query failed",
          },
        };
      },
      providesTags: ["EligibleCertifications"],
    }),

    createCertificationRecord: builder.mutation<APIResponse<inter.CertificationRecordDetails>, inter.CreateCertificationRecordRequest>({
      query: (body) => ({
        url: "auth/certification-records",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CertificationRecords", "EligibleCertifications"],
    }),

    updateCertificationRecord: builder.mutation<APIResponse<inter.CertificationRecordDetails>, { id: number; body: inter.UpdateCertificationRecordRequest }>({
      query: ({ id, body }) => ({
        url: `auth/certification-records/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "CertificationRecord", id },
        "CertificationRecords",
        "EligibleCertifications",
      ],
    }),

    voidCertificationRecord: builder.mutation<APIResponse<inter.CertificationRecordDetails>, { id: number; body: inter.VoidCertificationRecordRequest }>({
      query: ({ id, body }) => ({
        url: `auth/certification-records/${id}/void`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "CertificationRecord", id }, "CertificationRecords"],
    }),

    sendCertificationRecordToRevision: builder.mutation<APIResponse<inter.CertificationRecordDetails>, inter.SendToRevisionCertificationRecordRequest>({
      query: ({ id, body }) => ({
        url: `auth/certification-records/${id}/send-to-revision`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "CertificationRecord", id }, "CertificationRecords"],
    }),

    getCollectableCertificationRecords: builder.query<
      APIResponse<inter.PaginatedData<inter.CertificationRecordView>>,
      { search: string; page: number; size: number }
    >({
      query: (params) => ({
        url: "auth/certification-records/collectable",
        params: buildListParams(params),
      }),
      providesTags: ["CertificationRecords"],
    }),

    collectCertificationRecordItems: builder.mutation<APIResponse<inter.CertificationRecordDetails>, inter.CollectCertificationRecordItemsRequest>({
      query: ({ id, body }) => ({
        url: `auth/certification-records/${id}/pay`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "CertificationRecord", id }, "CertificationRecords"],
    }),
  }),
});

export const {
  useGetCertificationRecordSettingsQuery,
  useLazyGetCertificationRecordSettingsQuery,
  useGetCertificationRecordsQuery,
  useLazyGetCertificationRecordsQuery,
  useGetGeneratedCertificationRecordsQuery,
  useLazyGetGeneratedCertificationRecordsQuery,
  useGetRevisionCertificationRecordsQuery,
  useLazyGetRevisionCertificationRecordsQuery,
  useGetCertificationRecordByIdQuery,
  useLazyGetCertificationRecordByIdQuery,
  useGetCertificationRecordDetailQuery,
  useLazyGetCertificationRecordDetailQuery,
  useGetEligibleCertificationsForRecordQuery,
  useLazyGetEligibleCertificationsForRecordQuery,
  useCreateCertificationRecordMutation,
  useUpdateCertificationRecordMutation,
  useVoidCertificationRecordMutation,
  useSendCertificationRecordToRevisionMutation,
  useGetCollectableCertificationRecordsQuery,
  useLazyGetCollectableCertificationRecordsQuery,
  useCollectCertificationRecordItemsMutation,
} = certificationRecordsApi;
